// Geometry and vocabulary for the spend-target bar.
//
// Pure, so the awkward parts — a target that is already blown, a pending band
// that pushes the total past 100%, a period that has barely started — are
// testable without rendering anything.
//
// The bar shows THREE things at once, and they are easy to confuse:
//   approved   solid       — counted against the target. This is the number.
//   pending    hatched     — real spend sitting in unapproved documents
//                            (status טיוטה). NOT counted, because a budget must
//                            not move on unreviewed paperwork, but shown because
//                            it is what turns "75%, comfortable" into "112%".
//   pace       thin marker — where the period says you should be by now.
//
// Everything here is NET (ex-VAT) — targets are entered ex-VAT and spend is
// measured ex-VAT, so there is no basis to choose and nothing to re-derive.

// A blown target must stay legible: past this the bar stops growing and reports
// `overflowed` instead, so 900% doesn't squash the whole thing into a sliver.
const MAX_SCALE = 200;

export const STATUS_LABEL = {
  on_track:  'בקצב',
  ahead:     'מעל הקצב',
  over:      'חריגה',
  no_target: 'ללא יעד',
};

// ── the card's semantic palette ───────────────────────────────────────────────
//
// Warm, sage-leaning versions of the three verdicts, so this card sits on the
// parchment page (#F2F1EC) and its white card without the cool blue-grey cast
// the old slate/red pair had. `פירוט … מול יעדי תקציב` is the only card that
// draws a verdict from them (`רכש מול יעדי תקציב`, the other reader, was
// retired 2026-09-19; `AmberCallout` takes one text step and nothing else) —
// so they are that card's tokens, not app-wide ones.
//
// TWO ramps, because the same verdict has to work as a 12px bar fill AND as
// 12–17px text on #fff, and one hex cannot do both for amber:
//   STATUS_COLOR      bar fills (and the number, where it clears 4.5:1 anyway)
//   STATUS_TEXT_COLOR the same hue stepped dark enough for body text
// Contrast on #fff: #2D6A4F 6.4:1, #DC2626 4.8:1 — both clear AA. #D97706 is
// 3.2:1, fine for a bar (AA non-text is 3:1) and NOT fine for a 12px word, so
// `ahead` is the one status whose text step differs.
export const STATUS_COLOR = {
  on_track:  '#2D6A4F',
  ahead:     '#D97706',
  over:      '#DC2626',
  no_target: '#94a3b8',
};

export const STATUS_TEXT_COLOR = {
  ...STATUS_COLOR,
  ahead: '#B45309',
};

// The track behind every bar on this card. Warm sage-tinted neutral rather
// than the old cool #eef2f7 / #f4f6f8 greys, which read blue next to the
// forest-green fill.
export const TRACK_BG = '#E7ECE8';

export const statusLabel = (s) => STATUS_LABEL[s] || STATUS_LABEL.no_target;
export const statusColor = (s) => STATUS_COLOR[s] || STATUS_COLOR.no_target;
export const statusTextColor = (s) => STATUS_TEXT_COLOR[s] || STATUS_TEXT_COLOR.no_target;

/**
 * Widths (in % of the track) for one progress row.
 * Returns null when there is no target — there is nothing to be a fraction OF,
 * and a bar drawn against an invented denominator is worse than no bar.
 *
 * @param {object} p a /progress or /summary entry
 * @returns {{scale:number, approvedWidth:number, pendingWidth:number,
 *            targetAt:number, paceAt:number, overflowed:boolean}|null}
 */
export function barGeometry(p, { maxScale = MAX_SCALE } = {}) {
  if (!p || p.pct_of_target == null) return null;

  const approved = Math.max(0, p.pct_of_target);
  // projected should never be below approved, but it is derived server-side
  // from a different sum; clamp rather than render a negative pending band.
  const projected = Math.max(approved, p.projected_pct_of_target ?? approved);

  // The track always covers at least the full target, so the 100% line is
  // always on screen even when barely anything has been spent.
  const scale = Math.min(maxScale, Math.max(100, projected));
  const toWidth = (pct) => Math.min(100, (pct / scale) * 100);

  const approvedWidth = toWidth(approved);
  return {
    scale,
    approvedWidth,
    pendingWidth: Math.max(0, toWidth(projected) - approvedWidth),
    targetAt: toWidth(100),
    // elapsed_fraction is 0..1 of the period; as a share of the target it is
    // the same number, because pace is measured in target-percent.
    paceAt: toWidth(100 * (p.elapsed_fraction ?? 0)),
    overflowed: projected > scale,
  };
}

/**
 * The one-line summary under the bar, as parts the caller formats.
 * `pending` is null when nothing is awaiting approval — the whole band, its
 * legend and the projected figure all disappear together in that case, rather
 * than showing a confusing "טרם אושר ₪0".
 */
export function barSummary(p) {
  if (!p) return null;
  return {
    actual: p.actual_net,
    target: p.target,
    pending: p.draft_net > 0 ? p.draft_net : null,
    projectedPct: p.draft_net > 0 ? p.projected_pct_of_target : null,
    pct: p.pct_of_target,
  };
}
